import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  Snackbar,
  Typography,
  useTheme,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { selectFollowTopics, selectTopTopics } from '../../../store/slices/contentSlice';
import { addFollowTopic, deleteFollowTopic } from '../../../store/thunks/contentThunk';
import { TestIds } from '../../../assets/testIds';
import { getFormattedTestId, getSortedTopTopics, ITopic } from './ContentCommonFunctions';
import { TopicsCount } from '../../../common/models/content/TopicsCount';
import { Logger } from '../../../config/logging/logger';
import { useCustomEventTracking } from '../../../common/useCustomEventTracking';

interface FollowTopicProps {
  topicIndx: number
  homeCardFollowTopic?: boolean
}

interface FollowTopicsCardProps {
  isHomeCard: boolean
}

export function FollowTopicsCard({
  isHomeCard,
}: FollowTopicsCardProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const logger = Logger.create('FollowTopicsCard');
  const memberFollowedTopics = useAppSelector(selectFollowTopics);
  const [openErrorSnackbar, setOpenErrorSnackbar] = useState(false);
  const [loadingTopicIndex, setLoadingTopicIndex] = useState<number | null>(null);
  const { trackAccountSetting } = useCustomEventTracking();
  const allTopics = useAppSelector(selectTopTopics);
  const sortedTopTopics =
    useMemo(() => {
      const res = getSortedTopTopics(allTopics).map((obj: ITopic | TopicsCount) => ({
        ...obj,
        followed: false,
        dataTestId: `${TestIds.followTopics.btnPrefix}-${getFormattedTestId(obj.name)}-btn`,
      }));
      if (isHomeCard) return res.slice(0, 5);
      return res;
    }, [allTopics, isHomeCard]);

  const [followTopics, setFollowTopics] = useState(sortedTopTopics);

  useEffect(() => {
    const newFollowTopics = followTopics.map((item) => {
      const followedTopic =
        memberFollowedTopics.find((topic) => topic.tagName === item.name);
      return followedTopic ? { ...item, followed: true } : { ...item, followed: false };
    });
    setFollowTopics(newFollowTopics);
    setLoadingTopicIndex(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberFollowedTopics]);

  const handleSnackBarClose = () => {
    setOpenErrorSnackbar(false);
  };

  const handleFollowTopic = async (name: string, followed: boolean, topicIndx: number) => {
    setLoadingTopicIndex(topicIndx);
    try {
      if (followed) {
        await dispatch(addFollowTopic({ relationType: 'follow', tagName: name })).unwrap();
      } else {
        const topicToBeDeleted = memberFollowedTopics.find((topic) => topic.tagName === name);
        if (topicToBeDeleted) {
          await dispatch(deleteFollowTopic(topicToBeDeleted.id)).unwrap();
        }
      }
      trackAccountSetting(followed ? 'Follow' : 'Unfollow', 'Topic', { topic: name });
    } catch (e) {
      logger.error(Logger.format(e));
      setLoadingTopicIndex(null);
      setOpenErrorSnackbar(true);
    }
  };

  const onFollowBtnClick = (index: number) => {
    handleFollowTopic(followTopics[index].name, !followTopics[index].followed, index);
  };

  const FollowTopic = ({ topicIndx }: FollowTopicProps) => {
    const isLoading = loadingTopicIndex === topicIndx;
    return (
      <>
        <Button
          data-testid={followTopics[topicIndx].dataTestId}
          variant="outlined"
          sx={{
            fontSize: theme.spacing(2),
            fontWeight: 600,
            lineHeight: 1,
            letterSpacing: theme.spacing(0.0625),
            textAlign: 'center',
            borderRadius: 100,
            padding: `${theme.spacing(0.5)} ${theme.spacing(1.5)} ${theme.spacing(0.5)} ${theme.spacing(1)}`,
            margin: `0 ${theme.spacing(1)} ${theme.spacing(1)} 0`,
            marginBottom: isHomeCard ? theme.spacing(2.5) : theme.spacing(1),
            border: `${theme.spacing(0.25)} solid ${theme.palette.brand.primary}`,
            color: followTopics[topicIndx].followed ?
              theme.palette.common.white :
              theme.palette.brand.primary,
            backgroundColor: followTopics[topicIndx].followed ?
              theme.palette.brand.primary :
              theme.palette.common.white,
            [theme.breakpoints.down('sm')]: {
              fontSize: theme.spacing(1.5),
              padding: `${theme.spacing(0.625)} ${theme.spacing(2)} ${theme.spacing(0.625)} ${theme.spacing(1)}`,
              margin: `${theme.spacing(0)} ${theme.spacing(0.5)} ${theme.spacing(1)}`,
            },
            '&:hover': {
              backgroundColor: followTopics[topicIndx].followed ?
                theme.palette.brand.primary :
                theme.palette.brand.surface,
              color: followTopics[topicIndx].followed ?
                theme.palette.common.white :
                theme.palette.brand.primary,
              border: `${theme.spacing(0.25)} solid ${theme.palette.brand.primary}`,
            },
            '&:disabled': {
              backgroundColor: followTopics[topicIndx].followed ?
                theme.palette.brand.primary :
                theme.palette.brand.surface,
              color: followTopics[topicIndx].followed ?
                theme.palette.common.white :
                theme.palette.brand.primary,
              border: `${theme.spacing(0.25)} solid ${theme.palette.brand.primary}`,
            },
          }}
          onClick={() => onFollowBtnClick(topicIndx)}
          disabled={isLoading}
        >
          {followTopics[topicIndx].followed ? <CheckIcon /> : <AddIcon />}
          {followTopics[topicIndx].name}
        </Button>
      </>
    );
  };

  return (
    <Card sx={{
      minWidth: 275,
      width: '100%',
      borderRadius: theme.spacing(1.25),
      boxShadow: 0,
      ...(isHomeCard && {
        backgroundColor: theme.palette.common.white,
        color: theme.palette.brand.primary,
        pt: `${theme.spacing(0)} !important`,
        pr: `${theme.spacing(0)} !important`,
      }),
    }}>
      <Snackbar
        sx={{
          [theme.breakpoints.up('md')]: {
            width: '80%',
            maxWidth: theme.spacing(126.5),
          },
        }}
        open={openErrorSnackbar}
        autoHideDuration={4000}
        onClose={handleSnackBarClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <Alert severity="error" sx={{ width: '100%' }} onClose={handleSnackBarClose}>
          <AlertTitle>{t('error.generalMessage')}</AlertTitle>
        </Alert>
      </Snackbar>
      <CardContent sx={{
        position: 'relative',
        ...(isHomeCard && {
          pt: theme.spacing(0),
          '&:last-child': {
            pb: `${theme.spacing(0)} !important`,
          },
        }),
        p: isHomeCard ? theme.spacing(0) : theme.spacing(3),
        letterSpacing: theme.spacing(0.0625),
        [theme.breakpoints.down('sm')]: {
          p: theme.spacing(1),
        },
      }}>

        {!isHomeCard && <Typography variant='h2'>
          {t('preferences.followTopics.heading')}
        </Typography>}
        <Box
          sx={{
            mt: isHomeCard ? theme.spacing(0) : theme.spacing(3),
          }}
        >
          {sortedTopTopics.map((topic, index) => (
            <FollowTopic key={index} topicIndx={index} />
          ))}
        </Box>
      </CardContent>
    </Card>
  );
}
